<template>
  <div v-show="(display && carriers) || isMenuItemType('link')" :type="item.data.ItemType">
    <div v-if="isMenuItemType('group') && isValidGroup">
      <menu-item-folder :item="item" :level="level" :open="isOpen || undefined" :sidemenu="sidemenu" :isLoad="isLoading || undefined" @onOpenChange="onOpenChange" :class="isLoading ? 'menuitem-loading' : ''" type="group" />

      <menu-item-folder-dropdown
        v-if="isOpen"
        :item="item"
        :level="level"
        :reportCodeCounts="reportCodeCounts"
        :carriers="carriers"
        :activeSite="activeSite"
        :activeUser="activeUser"
        :open="isOpen || undefined"
        :isLoad="isLoading || undefined"
        :years="{
          menuItemId: item.id,
          years: years,
        }"
        @years="onYearsUpdate"
      />
    </div>

    <div v-else-if="isMenuItemType('dynamic Carrier') && carrierIsvalid()">
      <div :class="['sidemenu-mask-dropdown', isLoading ? 'menuitem-loading' : '']" @click="() => (isOpen = !isOpen)" :id="item.id" :data-children="numCarriers" :parent="item.data.ParentId" :ref="item.id">
        <div class="tooltip-hover" :open="undefined" :title="item.data.Label">
          <i :class="['status-icon fa fa-chevron-right', isOpen ? 'open' : 'closed']"></i>
          <i :class="getIcon(item.data)"></i>
          {{ item.data.Label }}
        </div>
      </div>

      <div class="sidemenu-mask-dropdown-list" :data-controlled-by="item.id" :open="isOpen || undefined" :level="getNextLevel() - 2" :type="item.data.ReportCode" :ref="item.id">
        <div v-for="carrier in validCarriers" :key="carrier.code">
          <SidemenuCarrierItem
            :reportTypeCodes="reportTypeCodes"
            :activeSite="activeSite"
            :activeUser="activeUser"
            :carriers="carriers"
            :reportCodeCounts="reportCodeCounts"
            :level="getNextLevel()"
            v-bind:item="carrier"
            @update="update"
            @is-invalid="handleEvent(-1)"
            @is-valid="handleEvent(1)"
          />
        </div>
      </div>
    </div>

    <div v-else-if="isMenuItemType('vendor')">
      <menu-item-folder :item="item" :level="level" :open="isOpen || undefined" :sidemenu="sidemenu" @onOpenChange="onOpenChange" />

      <menu-item-folder-dropdown
        v-if="isOpen"
        :item="item"
        :level="level"
        :reportCodeCounts="reportCodeCounts"
        :carriers="carriers"
        :activeSite="activeSite"
        :activeUser="activeUser"
        :open="isOpen || undefined"
        :isLoad="isLoading || undefined"
        :years="{
          menuItemId: item.id,
          years: years,
        }"
        @years="onYearsUpdate"
      />
    </div>

    <div v-show="item.isLink && ((item.linkRequiresPermission && hasPermission('USR')) || !item.linkRequiresPermission)">
      <div :class="['sidemenu-mask-dropdown', isLoading ? 'menuitem-loading' : '']" @click.stop="navigate(item.data)" :id="item.id" :data-children="5" :parent="item.data.ParentId" :ref="item.id">
        <div class="tooltip-hover" :open="isOpen || undefined" :title="item.data.Label">
          <i :class="getIcon(item.data)"></i>
          {{ item.data.Label }}
        </div>
      </div>
    </div>

    <div v-if="isMenuItemType('reports')">
      <menu-item-loader v-if="!reports && !isEmpty && isLoading" :level="level + 1" :menuItem="item" />
      <menu-item-reports v-else-if="reports && !isEmpty" :reports="reports" :menuItem="item" />
      <div v-if="!reports && isEmpty && !isLoading">
        <div class="sidemenu-mask-dropdown-list" :open="true" :level="getNextLevel()">
          <div class="sidemenu-mask-item" :level="level + 1">No files found</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeMount, provide } from 'vue'
import _ from 'underscore'
import Constants from '../constants'
import SidemenuCarrierItem from './partials/SidemenuCarrierItem.vue'
import MenuItemFolder from './MenuItemFolder.vue'
import MenuItemLoader from './MenuItemLoader.vue'
import MenuItemFolderDropdown from './MenuItemFolderDropdown.vue'
import MenuItemReports from './MenuItemReports.vue'
import axios from '../axios'
import { useRouter } from 'vue-router'
import { openPDFPreviewer } from './dialogs'
import { useGlobalUtils } from '../helpers/globalUtils'

const { successToast, downloadReport, errorToast, base64ToArrayBuffer, getPdfResponse } = useGlobalUtils()

const props = defineProps({
  item: {
    children: Array,
    data: Object,
    id: Number,
    isStatic: Boolean,
    level: Number,
    reportCodes: Array,
    reports: Array,
    carrierCode: String,
  },
  isLoad: Boolean,
  activeSite: Object,
  activeUser: Object,
  reportTypeCodes: Array,
  level: Number,
  carrierCode: {
    required: false,
    type: String,
  },
  carriers: {
    type: Array,
    default: function () {
      return []
    },
  },
  open: Boolean,
  reportCodeCounts: Object,
})

const emit = defineEmits()
const router = useRouter()

//
// define all refs and reactive variables
const isOpen = ref(false)
const sidemenu = ref(document.querySelector('.sidemenu-mask'))
const reportCodes = ref({})
const years = ref({})
const reports = ref(null)
const display = ref(true)
const numCarriers = ref(0)
const isLoading = ref(false)
const isEmpty = ref(false)

watch(isLoading, (isLoad) => {
  emit('is-load', isLoad)
})

watch(isOpen, (isOpen) => {
  if (isOpen) {
    isLoading.value = true

    getReports(() => {
      display.value = true
      emit('is-valid')
      isLoading.value = false
    })
  }
})

const isValidGroup = computed(() => {
  const childrenHasLinks = props.item.children.filter((child) => child.isLink).length > 0
  return props.item.reportCodes.length > 0 || props.item.children.length > 0 || childrenHasLinks
})

const validCarriers = computed(() => {
  let carriers = []

  props.reportCodeCounts.carriers.forEach(function (_reportType) {
    if (!carriers.includes(_reportType.carrierCode)) {
      carriers.push(_reportType.carrierCode)
    }
  })

  return props.carriers.filter(function (_carrier) {
    if (carriers.includes(_carrier.code)) {
      _carrier.template = _.map(props.item.children, _.clone)
      return _carrier
    }
  })
})

const isIE = computed(() => {
  var ua = navigator.userAgent
  return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1
})

onBeforeMount(() => {
  if (props.reportTypeCodes !== undefined) {
    localStorage.setItem('codes', JSON.stringify(props.reportTypeCodes))
    reportCodes.value = JSON.parse(JSON.stringify(props.reportTypeCodes))
  } else {
    reportCodes.value = JSON.parse(localStorage.getItem('codes'))
  }
})

const hasPermission = (code) => {
  return reportCodes.value.includes(code)
}

const shouldRenderMenuItemBasedOnCountsAndPermissions = () => {
  let key = ''
  let validChildrenNumber = 0
  let codesInQuestion = []
  const item = props.item
  const isCarrier = isMenuItemType('carrier') || props.item.isCarrier
  const isVendor = isMenuItemType('vendor') || props.item.isVendor

  if (isCarrier) {
    codesInQuestion = item.carrierCodes
    key = 'carrier'
    return true
  }

  if (isVendor) {
    codesInQuestion = item.vendorCodes
    key = 'vendor'
    return true
  }

  if (!isCarrier && !isVendor) {
    codesInQuestion = item.reportCodes
    key = 'reports'
  }

  // TODO:// Missing logic for documents.... We need re-determine how this is found as it seems to have been lost in the refactor.
  let permissions = reportCodes.value
  codesInQuestion.forEach((code) => {
    let reportCodeCounts = props.reportCodeCounts[key]
    let reportCodeCount = reportCodeCounts.find((c) => c.typeCode === code)
    if (reportCodeCount && reportCodeCount.count > 0 && permissions.includes(code)) {
      validChildrenNumber++
    }
  })

  const checkChildrenForLinks = (children) => {
    children.forEach((child) => {
      if (child.isLink && !child.linkRequiresPermission) {
        validChildrenNumber++
      }

      if (child.children) {
        checkChildrenForLinks(child.children)
      }
    })
  }

  checkChildrenForLinks(item.children)

  return validChildrenNumber > 0
}

const onOpenChange = (open) => {
  isOpen.value = open

  if (isOpen.value) {
    isLoading.value = true

    getReports(() => {
      display.value = true
      emit('is-valid')
      isLoading.value = false
    })
  } else {
    years.value = {} // We want to re-check anytime they open the menu.
  }
}

const onYearsUpdate = (years) => {
  years.value = years
}

const isMenuItemType = (type) => {
  let types = Constants.menu.type

  return parseInt(props.item.data.ItemType) === types[type]
}

const vendorIsValid = () => {
  let hasAtleastOne = false

  props.reportCodeCounts.vendors.forEach((vendor) => {
    let hasPermission = reportCodes.value.includes(vendor.typeCode)

    if (props.item.reportCodes.includes(vendor.typeCode) && parseInt(vendor.count) > 0 && hasPermission) {
      hasAtleastOne = true
    }
  })

  return hasAtleastOne
}

const navigate = (menuItem) => {
  router.push({ name: menuItem.Route })
}

const carrierIsvalid = () => {
  let hasAtleastOne = false

  props.reportCodeCounts.carriers.forEach((carrier) => {
    let hasPermission = reportCodes.value.includes(carrier.typeCode)

    if (props.item.reportCodes.includes(carrier.typeCode) && carrier.count > 0 && hasPermission) {
      hasAtleastOne = true
    }
  })

  return hasAtleastOne
}

const handleEvent = () => {}

const update = () => {}

const getNextLevel = () => {
  return props.level + 1
}

const handleReportResponse = (response, callback) => {
  const sideMenuMask = document.querySelector('.sidemenu-mask')
  const ReportCode = props.item.code

  let files = {}

  if (response.status === 200 && response.data.page.totalRecords > 0) {
    response.data.records.forEach((report) => {
      // If ReportCode is Valid for the search
      if (ReportCode === report.typeCode) {
        // Check if the year is already in the list. If not, add it
        if (files[report.yearOverride] === undefined) {
          files[report.yearOverride] = []
        }

        // If the report yeat is already in the list
        if (files[report.yearOverride] !== undefined) {
          // get an array of report ids from the current report year key in the list
          let ids = files[report.yearOverride].filter((_report) => {
            return parseInt(_report.id)
          })

          // Check if the report id is present in the array
          if (!ids.includes(parseInt(report.id))) {
            files[report.yearOverride].push({
              ...report,
              sidemenu: sideMenuMask,
            })
          }
        }
      }
    })

    if (!props.item.isStatic) {
      emit('years', {
        menuItemId: props.item.id,
        years: files,
      })
    }

    setTimeout(() => {
      reports.value = files
    }, 100)

    display.value = true
    emit('is-valid')
  } else {
    files = {}
    isEmpty.value = true
    if (parseInt(props.item.data.ItemType) === 2) {
      display.value = false
      emit('is-invalid')
    } else {
      display.value = true
      emit('is-valid')
    }
  }

  // this.isLoading = false
  callback(files)
}

const getReports = (callback) => {
  if (!isMenuItemType('reports')) {
    callback()
    return
  }

  const ReportCode = props.item.code

  if (ReportCode) {
    if (props.item.isDocument || isMenuItemType('carrier') || isMenuItemType('vendor') || props.item.isVendor) {
      axios
        .post(`/sites/menu/types`, {
          carrierCodes: [props.item.isVendor ? 'BCC' : props.item.carrierCodes[0]],
          reportTypeCodes: [props.item.data.ReportCode],
        })
        .then((resp) => handleReportResponse(resp, callback))
    } else {
      axios
        .post('/reports/search', {
          siteId: props.activeSite.id,
          userId: props.activeUser.id,
          institutionId: props.activeUser.institutionId,
          filter: {
            typeCode: ReportCode,
          },
        })
        .then((resp) => handleReportResponse(resp, callback))
    }
  } else {
    callback()
  }
}

const getIcon = (menuItem) => {
  return Constants.menu.icon[menuItem.IconType].value
}

async function getReport(report) {
  const path = report.path.replace('api/', '')

  let needsConversion = false

  if (report.extension.toLowerCase() === '.pdf' || report.extension.toLowerCase() === '.docx' || report.extension.toLowerCase() === '.pptx') {
    if (report.extension.toLowerCase() === '.pptx' || report.extension.toLowerCase() === '.docx') needsConversion = true

    let pdfResponse = await getPdfResponse(path.includes('document'), needsConversion, report.id, null, report)

    if (!pdfResponse) {
      errorToast('Failed to load document')
      isLoading.value = false
      return
    }

    let data = null
    let fileData = null
    let fileName = ''

    if (needsConversion) {
      if (typeof pdfResponse.data === 'string') {
        fileData = JSON.parse(pdfResponse.data)
      } else {
        fileData = pdfResponse.data
      }

      fileName = fileData.fileName
      data = base64ToArrayBuffer(fileData.file)
    } else {
      try {
        fileName = pdfResponse.headers['content-disposition'].split('filename=')[1]
      } catch {
        errorToast('Failed to load document')
        isLoading.value = false
        return
      }
      data = await new Response(pdfResponse.data).arrayBuffer()
    }

    const pdfPreviewer = await openPDFPreviewer(data, fileName)

    if (pdfPreviewer === 'download') {
      const adjustedPath = report.path.replace('documents/clients', 'documents').replace('api/', '')
      await downloadReport(adjustedPath)
    }
  } else {
    await downloadReport(path)
    return
  }
}

provide('getReport', getReport)

onMounted(() => {
  const canRenderBasedOnCodesAndPermissions = shouldRenderMenuItemBasedOnCountsAndPermissions()

  if (!canRenderBasedOnCodesAndPermissions && !isMenuItemType('vendor') && !isMenuItemType('carrier') && !isMenuItemType('link')) {
    display.value = false
    emit('is-invalid')
    return
  }

  if (isMenuItemType('carrier')) {
    if (!carrierIsvalid()) {
      display.value = false
      emit('is-invalid')
    } else {
      display.value = true
      emit('is-valid')
    }
  }

  // Make sure this is a menuItem with a report code that is a document which is not a BCC (vendor) document and actually has documents.
  if (
    props.item.code &&
    isMenuItemType('carrier') && // Added isCarrier check to prevent bleeding this logic into vendor docs. TODO:// determine if this is the correct logic.
    props.item.isDocument &&
    props.item.carrierCode !== 'BCC'
  ) {
    emit('is-invalid')
    display.value = false
  }

  /**
   * This is a static report type that is shown inline with the folder and
   * not broken down into years. Additionally, we should only render if the
   * user has access to the report type and the report type has documents.
   */
  if (isMenuItemType('reports') && props.item.isStatic && !canRenderBasedOnCodesAndPermissions) {
    display.value = false
    emit('is-invalid')
  }

  // if vendor grouping
  if (props.item.isVendor && !vendorIsValid()) {
    emit('is-invalid')
    display.value = false
  }

  if (isMenuItemType('reports') && !isMenuItemType('carrier')) {
    isLoading.value = true

    getReports(() => {
      display.value = true
      isLoading.value = false
      emit('is-valid')
    })
  }

  if (isMenuItemType('link')) {
    display.value = true
    emit('is-valid')
  }
})
</script>

<style scoped>
.menuitem-loading {
  animation: menuitem-loading 1s infinite !important;
  opacity: 0.25;
}

@keyframes menuitem-loading {
  0% {
    opacity: 0.25;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0.25;
  }
}
</style>
